import { clsx } from 'clsx'
import type { ReactElement } from 'react'

import { Tooltip } from '#design-system/component/Tooltip/Tooltip'

interface FieldFooterProps {
  /** DOM id used by the control's `aria-describedby` so screen readers associate the
   * hint with the input. */
  id: string
  /** 'helper' renders in secondary-text color; 'error' switches to destruction. */
  kind: 'helper' | 'error'
  /** Footer text. Truncates with a Tooltip showing the full string when it overflows. */
  children: string
}

/**
 * Helper-or-error text below the input shell. Wraps the text in a truncation-aware
 * Tooltip and colors via the bare `.error` state class.
 */
export const FieldFooter = ({ id, kind, children }: FieldFooterProps): ReactElement => (
  <Tooltip onlyShowIfReferenceTruncated title={children}>
    <p className={clsx('doss-field-hint', kind === 'error' && 'error')} id={id}>
      {children}
    </p>
  </Tooltip>
)

/** Decide what FieldFooter should display for a given pair of consumer-supplied messages.
 * `errorMessage` wins over `helperText` — when an error is present, the helper is hidden.
 * The shell's red error ring is gated by the same `errorMessage`, so footer and ring stay in
 * lockstep (red footer ⇔ red ring ⇔ `errorMessage` is set). */
export const resolveFooterMessage = (params: {
  errorMessage: string | undefined
  helperText: string | undefined
}): { kind: 'error' | 'helper'; text: string } | undefined => {
  if (params.errorMessage) {
    return { kind: 'error', text: params.errorMessage }
  }
  if (params.helperText) {
    return { kind: 'helper', text: params.helperText }
  }
  return undefined
}
